import { Component, OnInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReportModel } from './report.model';
import { Placeholder, Report, ReportAttributes } from './report.interface';
import { CustomerDispatcher } from '../../store/customer/customer-dispatcher';
import { OrchestrationService } from '../services/orchestration-service/orchestration.service';
import { TranslationService } from '../../common/services/languages/translation.service';
import { SnackBarService } from '../../common/services/snack-bar/snack-bar.service';
import { ApiResponseDispatcher } from '../../store/api-response/api-response-dispatcher';
import { select } from '@angular-redux/store';
import { Observable } from 'rxjs';
import { ApiResponseState } from '../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../store/api-response/api-response-actions';
import { CommonDispatcher } from '../../store/common/common-dispatcher';
import { MatFormField, MatLabel, MatSelect, MatOption, MatSuffix } from '@angular/material/select';
import { NgFor } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-report-form',
    templateUrl: './report-form.component.html',
    styleUrls: ['./report-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, NgFor, MatOption, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, TranslatePipe]
})
export class ReportFormComponent implements OnInit {
  /**
   *@var to store  input fields.
   **/
  public report_attributes: UntypedFormGroup;
  /**
   *@var to store the status of the form submission
   **/
  public form_status: string;
  /**
   *@var to store the placeholders of the form
   **/
  public placeholder: Placeholder;
  /**
   *@var to store the model
   **/
  public model: ReportModel;
  /**
   *@arg  observe api variable in redux
   **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public fb: UntypedFormBuilder,
    public translate: TranslationService,
    private apiDispatcher: ApiResponseDispatcher,
    public dispatcher: CustomerDispatcher,
    private snackBar: SnackBarService,
    public orch: OrchestrationService,
    public customerDispatcher: CustomerDispatcher,
    public common: CommonDispatcher
  ) {
    this.apiObservable.subscribe((api) => {
      if (
        [
          ApiResponseActions.SET_TCI_REPORT,
          ApiResponseActions.SET_SXM_REPORT,
          ApiResponseActions.SET_TELEMATICS_REPORT,
        ].includes(api.apiAction)
      ) {
        let responseMessages: string | null;
        switch (true) {
          case !!api.tciReport['type']:
            if (!!api.tciReport['status']) {
              responseMessages = api.tciReport['status'].messages
                .map((message) => this.translateOf(message.description))
                .join(', ');
              this.apiDispatcher.setTciReport({});
            }
            this.snackBar.showToast(
              responseMessages || this.translateOf('error')
            );
            this.clear();
            break;
          case !!api.sxmReport['type']:
            if (!!api.sxmReport['status']) {
              responseMessages =
                api.sxmReport['status'].messages
                  .map((message) => this.translateOf(message.description))
                  .join(', ') || null;
              this.apiDispatcher.setSxmReport({});
            }
            this.snackBar.showToast(
              responseMessages || this.translateOf('error')
            );
            this.clear();
            break;
          case !!api.telematicsReport['type']:
            if (!!api.telematicsReport['status']) {
              responseMessages =
                api.telematicsReport['status'].messages
                  .map((message) => this.translateOf(message.description))
                  .join(', ') || null;
              this.apiDispatcher.setTelematicsReport({});
            }
            this.snackBar.showToast(
              responseMessages || this.translateOf('error')
            );
            this.clear();
            break;
        }
      }
    });
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit(): void {
    this.setDefaultReports();
  }
  /**
   * @method Reports Returns list of reports
   * @returns list of Report
   */
  public get Reports(): Array<Report> {
    return this.model.reports;
  }
  /**
   * @method ReportOf Returns list of specific reports on selected report in overall
   * @returns list of
   */
  public get ReportsOf(): Array<ReportAttributes> {
    const reportName: string = this.report_attributes.value.reportOn;
    if (reportName !== null) {
      return this.model.findReportOf(reportName);
    }
    return [];
  }
  /**
   * setDefaultReports Defaults reports that set for the form
   **/
  public setDefaultReports(): void {
    this.model = new ReportModel();
    this.placeholder = this.model.placeholders;
    this.report_attributes = this.fb.group(this.model.formConfigs);
  }
  /**
   * startDateMin returns a date which is minimum date for end date calendar,
   * That is in endDate calendar the date before 'startDateMin' which are disabled once startDate is selected by user
   **/
  public get startDateMin(): Date {
    return this.report_attributes.value.startDate;
  }
  /**
   * endDateMax returns a date which is maximum date for start date calendar,
   * That is in startDate calendar the date after 'endDateMax' which are disabled once endDate is selected by user
   **/
  public get endDateMax(): Date {
    return this.report_attributes.value.endDate;
  }
  /**
   * searchProfile Initiates the method calls to search the respective profile
   **/
  public generateReport(): void {
    this.model.error = false;
    this.defaults();
    this.orch.getReport(this.model.formAttributes);
  }
  /**
   * setd the default redux
   **/
  public defaults(): void {
    this.common.resetAll();
    this.model.setFormAttributes(this.report_attributes);
  }
  /**
   * clear clear action
   **/
  public clear(): void {
    this.model.clearFormAttributes();
  }
  /**
   * add translator meesage for the given report name
   **/
  public translateOf(messageDescription: string): string {
    const reportNameIndex = messageDescription.indexOf('Report');
    const reportName = messageDescription.substring(0, reportNameIndex);
    if (!!reportName) {
      const translation_text = this.translate.getCustomTranslation(
        `report-message.${reportName.toLowerCase().trim().replace(/\s/g, '-')}`
      )['value'];
      return translation_text.includes('report-message')
        ? this.translate.getCustomTranslation(`report-message.error`)['value']
        : translation_text;
    } else {
      return messageDescription.includes('success')
        ? this.translate.getCustomTranslation(`report-message.success`)['value']
        : this.translate.getCustomTranslation(`report-message.error`)['value'];
    }
  }
}
